import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import {
  formatLocalDateOnlyForWire,
  formatLocalDatetimeForWire,
} from '@/shared/lib/date-format';
import type {
  HealthInsurance,
  PayerDetails,
} from '@/services/scheduling/use-prebook-selected-patient';

/**
 * POST /api/Patients/HealthInsurance
 *
 * **Why POST for an "edit" flow?** This is a list-replace upsert, not
 * a per-row create. The body is the patient's COMPLETE health-insurance
 * array — existing rows (with `healthInsuranceId`), new rows (no id),
 * and soft-deleted rows (with `isDeleted: true`) all ship together,
 * and the backend reconciles the persisted set against what we send.
 * No PUT/{id} or PATCH endpoint exists for this concept; the dev
 * frontend and Flutter both use POST against the same path with the
 * same shape (confirmed against `patient_data_source_impl.dart:311–323`
 * + a working dev curl, 2026-05-11). Reviewer note left here so this
 * doesn't look like a RESTful mistake on first read.
 *
 * Mirrors Flutter `saveInsuranceList()` in
 * `patient_data_source_impl.dart:311–328`. Body is the full
 * `HealthInsurance[]` array — the backend replaces the patient's list
 * with what we send.
 *
 * Wire-shape rules (verified against a working dev curl, 2026-05-11):
 *
 *   - Every field the backend reads MUST be present on every row.
 *     Missing fields cause a 500 (model binder NREs). Defaults below
 *     mirror what dev sends — empty strings for nullable strings,
 *     empty arrays for nullable lists, `false` / `0` for primitives.
 *   - **Dates ship without the `Z` suffix.** .NET's DateTime binder
 *     for these endpoints interprets the wire string as local time;
 *     a trailing `Z` from `toISOString()` makes it choke or
 *     double-shift. `toWireDate()` below normalises the two date fields to
 *     day-preserving `YYYY-MM-DDT00:00:00.000` form; `nowLocalIso()`
 *     stamps `submittedDate` off the LOCAL clock (see PROD-1778 notes).
 *   - **`insuranceProvider.relatedTpa` ships as `[]`, not `null`** —
 *     the dev payload always has the array (even when empty). Same
 *     for `imageUrl`: it ships as `null` (not `""`) on the provider
 *     object.
 *   - **`submittedDate` is set to now on every save** — and the backend
 *     PERSISTS it verbatim; it does NOT re-stamp server-side (verified
 *     against the .NET source during PR #778 review). See `nowLocalIso`.
 *   - Soft-deleted-but-never-saved rows are stripped before send
 *     (Flutter does the same filter).
 */

interface SaveHealthInsuranceParams {
  patientId: string;
  insuranceList: HealthInsurance[];
}

/**
 * Normalise `eligiblityStartDate` / `expirationDate` for the wire. Returns null
 * for falsy input so the field round-trips correctly when blank.
 *
 * PROD-1778: was `iso.slice(0, -1)` on `Z`-suffixed input. That is a FALSE
 * safeguard — `toISOString()` has already converted the instant to UTC, so
 * dropping the `Z` bakes the offset in rather than removing it, and the .NET
 * binder stores the literal.
 *
 * These two fields are CALENDAR DAYS (both editors are `<input type="date">`),
 * so the correct normaliser is the day-preserving one, NOT
 * `formatLocalDatetimeForWire` — re-deriving local components from a
 * UTC-midnight instant would move the day for anyone west of UTC. On today's
 * feeders, which ship `YYYY-MM-DDT00:00:00.000`, this is a strict no-op; it now
 * stays correct if a `toISOString()` value ever reaches it again.
 */
function toWireDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return formatLocalDateOnlyForWire(iso) || null;
}

/**
 * Now() in the .NET-friendly format the dev curl uses.
 *
 * PROD-1778: this used `getUTC*` getters, so `submittedDate` shipped UTC
 * wall-clock stamped as if it were local — 4h off in UAE. Now reads the local
 * clock, matching Flutter's `DateTime.now().toIso8601String()`.
 *
 * The old comment here asserted the backend re-stamps this audit field
 * server-side, which would have made the value moot. **That was wrong**, and it
 * was checked against the .NET source rather than left as an assumption
 * (review of PR #778):
 *
 *   `Scrips.Patient/Infrastructure/Implementations/Services/Patient/
 *    PatientInsuranceService.cs` writes `SubmittedDate = item.SubmittedDate`
 *    verbatim on BOTH the insert (:269) and the update (:335) branch — and the
 *    same on the corporate endpoint (:483 / :548). The adjacent `CreatedTs` /
 *    `UpdatedTs` ARE stamped with `DateTime.UtcNow` on neighbouring lines, so
 *    the distinction is deliberate. No EF default, no interceptor, no trigger.
 *
 * So the client value IS persisted, and this fix changes what lands in the
 * column on every save — in the correct direction.
 *
 * One reason it was invisible: the column is effectively WRITE-ONLY. Every GET
 * substitutes `CreatedTs` for it (`PatientInsuranceService.cs:153` and `:188`,
 * used by every read path including the patient GET this app renders from), so
 * no app surface ever displayed the wrong value. Only direct DB / report / BI
 * readers of the column would have seen it.
 */
function nowLocalIso(): string {
  return formatLocalDatetimeForWire(new Date());
}

interface WireProvider {
  id: string | null;
  imageUrl: string | null;
  payerName: string | null;
  payerCompanyId: string | null;
  address: string | null;
  office: string | null;
  website: string | null;
  email: string | null;
  payerAgreementName: string | null;
  payerAgreementId: string | null;
  phoneNumber: string | null;
  note: string | null;
  relatedTpa: unknown[];
}

function toWireProvider(p: PayerDetails | undefined): WireProvider | null {
  if (!p) return null;
  // Cast through unknown to pull the extension fields the dev payload
  // includes — they're not on our minimal PayerDetails type but the
  // backend echoes them back when we save.
  const ext = p as PayerDetails & {
    payerCompanyId?: string;
    payerAgreementName?: string;
    payerAgreementId?: string;
    address?: string;
    office?: string | null;
    website?: string | null;
    email?: string | null;
    phoneNumber?: string;
    note?: string | null;
    relatedTpa?: unknown[] | null;
  };
  return {
    id: p.id ?? null,
    imageUrl: p.imageUrl?.trim() ? p.imageUrl : null,
    payerName: p.payerName ?? p.name ?? null,
    payerCompanyId: ext.payerCompanyId ?? null,
    address: ext.address ?? null,
    office: ext.office ?? null,
    website: ext.website ?? null,
    email: ext.email ?? null,
    payerAgreementName: ext.payerAgreementName ?? null,
    payerAgreementId: ext.payerAgreementId ?? null,
    phoneNumber: ext.phoneNumber ?? null,
    note: ext.note ?? null,
    relatedTpa: ext.relatedTpa ?? [],
  };
}

interface WireRow {
  healthInsuranceId: string;
  patientId: string;
  agreementId: string;
  agreementName: string;
  insuranceProviderId: string;
  insuranceProvider: WireProvider | null;
  policyNumber: string;
  frontPhoto: string;
  backPhoto: string;
  frontPhotoUrl: string;
  backPhotoUrl: string;
  eligiblityStartDate: string | null;
  expirationDate: string | null;
  submittedDate: string;
  isPrimary: boolean;
  isReviewed: boolean;
  isDeleted: boolean;
  isVerified: boolean;
  copaymentType: string;
  billingGroups: unknown[];
}

function toWireRow(row: HealthInsurance, patientId: string): WireRow {
  return {
    healthInsuranceId: row.healthInsuranceId ?? '',
    patientId: row.patientId ?? patientId,
    agreementId: row.agreementId ?? '',
    agreementName: row.agreementName ?? '',
    insuranceProviderId:
      row.insuranceProviderId ?? row.insuranceProvider?.id ?? '',
    insuranceProvider: toWireProvider(row.insuranceProvider),
    policyNumber: row.policyNumber ?? '',
    // `frontPhoto` / `backPhoto` are the base64-only fields the legacy
    // backend kept around; dev still ships empty strings for both.
    frontPhoto: '',
    backPhoto: '',
    frontPhotoUrl: row.frontPhotoUrl ?? '',
    backPhotoUrl: row.backPhotoUrl ?? '',
    eligiblityStartDate: toWireDate(row.eligiblityStartDate),
    expirationDate: toWireDate(row.expirationDate),
    submittedDate: nowLocalIso(),
    isPrimary: !!row.isPrimary,
    isReviewed: row.isReviewed ?? true,
    isDeleted: !!row.isDeleted,
    isVerified: !!row.isVerified,
    copaymentType: row.copaymentType ?? '',
    billingGroups: row.billingGroups ?? [],
  };
}

export function useSaveHealthInsurance() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, SaveHealthInsuranceParams>({
    mutationFn: ({ patientId, insuranceList }) => {
      const cleaned = insuranceList.filter((row) => {
        if (row.healthInsuranceId) return true;
        const hasProvider = !!row.insuranceProvider?.id;
        const hasPolicy = !!row.policyNumber?.trim();
        const hasExpiry = !!row.expirationDate;
        return hasProvider && hasPolicy && hasExpiry;
      });
      const body = cleaned.map((row) => toWireRow(row, patientId));
      return post<unknown>('/api/Patients/HealthInsurance', body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient'] });
    },
  });
}
